import React from 'react';
import { Container, Stack, useTheme } from '@mui/system';
import { Box, Typography } from '@mui/material';

const generalFaqs = [
  {
    question: `Where did the questions come from?`,
    answer: `I wrote them all, 300k and counting.`,
  },
  {
    question: `Uhhhh… what?`,
    answer: `It’s a mixture of ETL/algorithmic writing and longhand, but yes, it’s taken quite a bit of effort to get it to this point.`,
  },
  {
    question: `How are the PARs set?`,
    answer: `PARs are generally set by assigning a maximum value of a Pterodactyl to the first keyword in a clue. Sometimes if it’s a very esoteric but still getable first clue, I may assign -7 or even -8 to the point in time when that word shows up. This is the basic premise, but there is a lot of variation to keep the game interesting - not the least of which is how much time elapses between keywords as the clue progresses. This creates different variations for how much of a point-gap certain clues can create, but remember that what’s easy for you may be hard for your opponent and vice-versa.`,
  },
  {
    question: `Question XYZ sucks.`,
    answer: `100k+, not all of them are going to be 5-star! Coming soon is a feature to rate questions thumbs up or down, so we can periodically purge the lame ones.`,
  },
  {
    question: `Question XYZ is incorrect.`,
    answer: `You may use the “report inaccuracy” button either during the question screen or in the post-game scoresheet. Remember to read the blurb in there about what counts as an inaccuracy or not in the game of PAR.`,
  },
  {
    question: `Explain the penalty system one more time?`,
    answer: `If you guess incorrectly BEFORE THE LETTERS APPEAR, you will:

    1) be assessed one penalty stroke,

    2) be locked out from answering for 1 full second, and

    3) you will show your opponent your wrong answer in a Match game. #3 is very critical, since in the case of false Pavlovs or a 50/50 toss-up, it’s a sizable risk to guess and get it wrong, because by showing your opponent your wrong answer you may be helping them arrive at the right answer. After the letters appear, none of these penalties apply.`,
  },
];

const casualProFaqs = [
  {
    question: `What are the biggest differences?`,
    answer: `1) Pro has a waaaaay harder set of answers. For example, if a Casual question is asking about an architect, it’s probably going to be 1 of the 10 most famous ones; if a Pro question is asking about an architect – it might be Moshe Safdie or Bjarke Ingels. There are many questions in Pro where only subject-matter-experts can score below PAR. 
    
    2) No fuzzy matching. Spelling must be perfect in Pro mode, although I do my best to offer “alternative acceptable answers” in as much fairness as possible (more on this later). The main purpose of this rule is so a false-positive or a false-negative in the matching algo doesn’t ruin a tournament game one day. Remember, a key differentiating advantage of this game is that because adjudication can be mostly performed pre-match, the format becomes most viable for live-viewing experiences and all the benefits that confers. 
    
    3) No reminder if last names, first names, or other partials are acceptable. “George Bush” is not accepted for either president. “Williams” is not accepted for either tennis-playing sister. Casual mode reminds you which names (or sports teams, for example) are acceptable with only a last-name (or in other cases, first-name) basis. In Pro, you’ll have to either remind yourself to knock out the ambiguity ahead of time, or bite the bullet and risk a “be more specific”.`,
  },
  {
    question: `Pro is too hard.`,
    answer: `Remember, your opponent will likely struggle with these difficulties as much as you do. As long as you’re playing against someone of equal skill, only the gap in your scores matter, and you shouldn’t get too hung up on your absolute score. Remember also it is designed to pose the fairest and greatest challenge to the elite trivia players of the world.`,
  },
  {
    question: `I find myself unable to get Ostriches or Pterodactyls in Casual.`,
    answer: `Many easy-difficulty questions are scored as to not give -4, -5, or -6 potential to clues considered too easy. For example, the question “The capital of this state is Denver” will not afford you the opportunity for a big score when the keyword shows up, to keep a certain integrity for the game’s scoring system. Even though Pro is much harder than Casual, you may that it’s actually easier to obtain the bigger scores in Pro if an opportunity arises and the question is in a subject you’re familiar with. This may be useful when playing for certain achievements that require the big scores.`,
  },
];

const rankedFaqs = [
  {
    question: `How is my MMR calculated?`,
    answer: `When you play against someone of roughly equal skill, a win or loss will increase or decrease your MMR by roughly equal amounts. When you play against someone of higher skill, wins will increase your MMR more while losses will decrease MMR less – and vice-versa for the opposite scenario.`,
  },
  {
    question: `How is Sit & Go MMR calculated?`,
    answer: `First, we look at the average MMR for all the players in the game. If you’re higher than this average, you need to finish higher in the leaderboard to get an increase, whereas if you’re lower than the average, you might not need to finish as high. Example: you – 1500, average – 1500 in a 10-player game: top-5 finish earns MMR, bottom-5 finish loses MMR. But, if you – 2000, average – 1500 in a 10-player game: you may need to finish in the top 3 to earn MMR but finishing in the bottom 7 will lose you MMR. Second, the higher you finish, the greater your increase (and obviously, the lower you finish, the greater your decrease).`,
  },
  {
    question: `What is the purpose of MMR?`,
    answer: `The intention is that we have devised a system where only your wits and skills determine whether you’ll receive a W or an L against your opponent. Because you’re both seeing the same questions, and because your ability to answer them faster than your opponent matters at a granular level, it should be capturing as much signal and as little noise as possible when comparing skill levels. Therefore, the result is that the players who rank most highly in MMR should be considered no less the pinnacle of the trivia world than Magnus Carlsen with his chess ELO rating or a pro tennis player with their ATP points.`,
  },
];

const intricaciesFaqs = [
  {
    question: `What are some tricks?`,
    answer: `When the letters appear, there are often big sources of help. A very short or long number of blanks can usually be a hint. The appearance of multiple “_ _” or “_ _ _” words may hint at where filler words like “the” or “and” appear. We give you punctuation for free, so a colon or apostrophe can sometimes be supremely helpful. Sometimes the sheer number of letters is a huge help (automaker with “_ _ _ _”? You’re probably down to a few choices and the first letter-reveal will probably give it away). Lastly, even if you forget if a last-name-only is acceptable or you have an alternative answer you’re not sure about, just give it a shot! The reward may outweigh the risk. And if you think you can make a solid academic case as to why your alternative answer should be accepted, you can always report the inaccuracy afterwards and I will review and curate as appropriate.`,
  },
  {
    question: `Your alternative answer acceptance for question XYZ was too lenient/strict.`,
    answer: `This is a very touchy subject, and the short answer is that it may never be perfect. However, for the quality-of-life for the players, I will curate the questions in the most consistent and fair manner I can think of. For alternative spellings, I will always take what Wikipedia accepts are common alternate spellings in the top blurb. For nicknames and such, I will do my best to afford every reasonable alternative possible (e. g. for Franklin Roosevelt, to allow FDR, F Roosevelt, FD Roosevelt, Franklin D Roosevelt, etc. etc. and as many alternates as I can think of). For tournament questions especially, plenty of research will go into every question to make sure that all fair alternative answers may be acceptable. Lastly, I’d like to draw an analogy to another game to demonstrate how the alternative answer problem can be mitigated. In Scrabble, high-level players always memorize what 2-letter words are acceptable, even if they don’t know what most of those words even mean. This is so that players may utilize effort in lieu of pure knowledge to level the playing field. Therefore, in the game of PAR, I will post every acceptable alternative answer and prompt in a 3-column CSV that can be found here (coming soon). Therefore, you may either choose to go off intuition, or you may try to manually learn what some of the common alternatives are. This affords the most hardcore players the opportunity to actually replace subjectivity with pure effort. The only caveat in this list is that when a nickname or alternative is PRESENT IN THE QUESTION, it will no longer be acceptable as an answer (e. g. “This president nicknamed FDR blah blah blah”; even though FDR will show up in the CSV as an acceptable answer for Franklin Roosevelt typically, for this particular question it will NOT be).`,
  },
];
const tournamentsFaq = [
  {
    question: `How will tournaments work?`,
    answer: `Weekly tournaments will occur every Monday night at a TBD time slot. Each week will have a theme of one of the categories, plus 4 Majors (the big categories combined) and one Grand Championship encompassing all categories at the end of a season. There will be 2 rounds using Sit &amp; Go style to cut entrants: the first cut will remove half the players and the second cut will remove everyone except the Top 8. The Top 8 will then enter a bracket-style tournament. Extra care will be taken to write the questions for tournaments; they will always be brand new and held to the highest standards of accuracy, wording, research, and alternatives/prompts. Winning a tournament will be extremely prestigious.`,
  },
];

const whatsNextFaq = [
  {
    question: `What are some of the coming features?`,
    answer: `Here are some: saving flashcards, friends’ lists and team games, daily games (like Wordle or NYT Crossword), more badges and achievements, finishing the framework for the tournaments, more art and sound, a save replay feature, a “Rush” mode, a Speedrun mode, to name a few.`,
  },
  {
    question: `What will be frequent updates to the core game?`,
    answer: `I will be continually writing new questions all the time, the site will be constantly improved and optimized, and we will eventually put out a legit phone browser experience as well as phone app.`,
  },
  {
    question: `What are the communities I should be joining?`,
    answer: `For starters, the discord for talking about the game can be found here. The Twitter page can be found at twitter.com/ParTrivia. Instagram, FB Group, and subreddit will follow soon.`,
  },
];

export const Faq = () => {
  return (
    <Container sx={{ paddingBottom: 10 }}>
      <Typography variant="h4" textAlign={'center'} mb={4}>
        Frequently Asked Questions
      </Typography>
      <Typography variant="h5" fontWeight={600} mb={2} color="secondary.main">
        General
      </Typography>
      {generalFaqs.map((o) => (
        <>
          <Stack direction={'row'} spacing={1} mt={2}>
            <Typography color="success.main">Q:</Typography>
            <Typography variant="body1">{o.question}</Typography>
          </Stack>
          <Stack direction={'row'} spacing={1} mt={1} mb={1}>
            <Typography color="ranked.main">A:</Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
              {o.answer}
            </Typography>
          </Stack>
        </>
      ))}
      <Typography
        variant="h5"
        fontWeight={600}
        mt={4}
        mb={2}
        color="secondary.main"
      >
        Casual vs Pro
      </Typography>
      {casualProFaqs.map((o) => (
        <>
          <Stack direction={'row'} spacing={1} mt={2}>
            <Typography color="success.main">Q:</Typography>
            <Typography variant="body1">{o.question}</Typography>
          </Stack>
          <Stack direction={'row'} spacing={1} mt={1} mb={1}>
            <Typography color="ranked.main">A:</Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
              {o.answer}
            </Typography>
          </Stack>
        </>
      ))}
      <Typography
        variant="h5"
        fontWeight={600}
        mt={4}
        mb={2}
        color="secondary.main"
      >
        Ranked
      </Typography>
      {rankedFaqs.map((o) => (
        <>
          <Stack direction={'row'} spacing={1} mt={2}>
            <Typography color="success.main">Q:</Typography>
            <Typography variant="body1">{o.question}</Typography>
          </Stack>
          <Stack direction={'row'} spacing={1} mt={1} mb={1}>
            <Typography color="ranked.main">A:</Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
              {o.answer}
            </Typography>
          </Stack>
        </>
      ))}
      <Typography
        variant="h5"
        fontWeight={600}
        mt={4}
        mb={2}
        color="secondary.main"
      >
        Intricacies
      </Typography>
      {intricaciesFaqs.map((o) => (
        <>
          <Stack direction={'row'} spacing={1} mt={2}>
            <Typography color="success.main">Q:</Typography>
            <Typography variant="body1">{o.question}</Typography>
          </Stack>
          <Stack direction={'row'} spacing={1} mt={1} mb={1}>
            <Typography color="ranked.main">A:</Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
              {o.answer}
            </Typography>
          </Stack>
        </>
      ))}
      <Typography
        variant="h5"
        fontWeight={600}
        mt={4}
        mb={2}
        color="secondary.main"
      >
        Tournaments
      </Typography>
      {tournamentsFaq.map((o) => (
        <>
          <Stack direction={'row'} spacing={1} mt={2}>
            <Typography color="success.main">Q:</Typography>
            <Typography variant="body1">{o.question}</Typography>
          </Stack>
          <Stack direction={'row'} spacing={1} mt={1} mb={1}>
            <Typography color="ranked.main">A:</Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
              {o.answer}
            </Typography>
          </Stack>
        </>
      ))}
      <Typography
        variant="h5"
        fontWeight={600}
        mt={4}
        mb={2}
        color="secondary.main"
      >
        What's Next?
      </Typography>
      {whatsNextFaq.map((o) => (
        <>
          <Stack direction={'row'} spacing={1} mt={2}>
            <Typography color="success.main">Q:</Typography>
            <Typography variant="body1">{o.question}</Typography>
          </Stack>
          <Stack direction={'row'} spacing={1} mt={1} mb={1}>
            <Typography color="ranked.main">A:</Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
              {o.answer}
            </Typography>
          </Stack>
        </>
      ))}
    </Container>
  );
};
